/**
 * Visitor Lead Scoring — Lightweight Event Tracker
 *
 * Collects RAW behavioural data (page views, scroll depth, clicks, time-on-site)
 * and ships them to the backend via batched sendBeacon calls.
 * The AI scoring engine on the backend analyzes this data for intent.
 *
 * Design constraints:
 *  - Zero performance impact on host website (requestIdleCallback, passive listeners, sendBeacon)
 *  - No cookies — uses existing localStorage visitor ID
 *  - No external dependencies — pure browser APIs
 *  - No intent inference — browser only collects raw events, AI handles scoring
 */
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DEFAULT_FLUSH_INTERVAL = 10000; // 10 seconds
const SCROLL_THROTTLE_MS = 500;
// ---------------------------------------------------------------------------
// Tracker Class
// ---------------------------------------------------------------------------
export class VisitorTracker {
    constructor(config) {
        this.queue = [];
        this.flushTimer = null;
        this.started = false;
        // State for derived signals
        this.pageEnteredAt = Date.now();
        this.totalEngagedMs = 0;
        this.isVisible = true;
        this.maxScrollDepth = 0;
        this.pagesViewed = [];
        this.isReturnVisit = false;
        this.referrer = null;
        this.utmParams = {};
        this.config = {
            widgetId: config.widgetId,
            visitorId: config.visitorId,
            endpoint: config.endpoint || '/api/events',
            flushIntervalMs: config.flushIntervalMs || DEFAULT_FLUSH_INTERVAL,
        };
        // Pre-bind handlers
        this.handleScroll = this.throttle(this.onScroll.bind(this), SCROLL_THROTTLE_MS);
        this.handleClick = this.onClick.bind(this);
        this.handleVisibility = this.onVisibilityChange.bind(this);
        this.handlePopState = this.onNavigation.bind(this);
        this.handleHashChange = this.onHashChange.bind(this);
        this.handleBeforeUnload = this.onBeforeUnload.bind(this);
        this.origPushState = history.pushState.bind(history);
        this.origReplaceState = history.replaceState.bind(history);
    }
    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------
    /** Start tracking. Wrapped in requestIdleCallback so it never blocks rendering. */
    start() {
        if (this.started)
            return;
        const init = () => this.init();
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(init);
        }
        else {
            setTimeout(init, 1);
        }
    }
    /** Stop tracking and flush remaining events. */
    stop() {
        if (!this.started)
            return;
        this.removeListeners();
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
        this.flush();
        this.started = false;
    }
    /** Push a custom event (e.g. from chat interactions). */
    track(type, data) {
        this.queue.push(Object.assign({ type, ts: Date.now() }, data));
    }
    // -------------------------------------------------------------------------
    // Initialization
    // -------------------------------------------------------------------------
    init() {
        this.started = true;
        // Detect return visit
        this.isReturnVisit = this.detectReturnVisit();
        // Capture referrer & UTM on first load
        this.referrer = document.referrer || null;
        this.utmParams = this.extractUtmParams();
        // Record initial page view (raw URL — AI will interpret intent)
        this.recordPageView(location.href);
        // Track referrer/UTM once
        if (this.referrer || Object.keys(this.utmParams).length > 0) {
            this.track('referral', Object.assign({ referrer: this.referrer }, this.utmParams));
        }
        // Track return visit signal
        if (this.isReturnVisit) {
            this.track('return_visit');
        }
        // Attach listeners
        this.addListeners();
        // Start flush interval
        this.flushTimer = setInterval(() => this.flush(), this.config.flushIntervalMs);
        // Mark that this visitor has been seen (for return-visit detection)
        this.markVisitorSeen();
    }
    // -------------------------------------------------------------------------
    // Event Handlers
    // -------------------------------------------------------------------------
    onScroll() {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        if (docHeight <= 0)
            return;
        const depth = Math.round((scrollTop / docHeight) * 100);
        if (depth > this.maxScrollDepth) {
            this.maxScrollDepth = depth;
        }
    }
    onClick(e) {
        var _a;
        const target = e.target;
        if (!target)
            return;
        // Walk up to find a clickable element (button, link, [data-cta])
        const el = target.closest('a, button, [data-cta], [role="button"]');
        if (!el)
            return;
        // Capture raw click data — AI will determine if it's a CTA
        const text = ((_a = el.textContent) === null || _a === void 0 ? void 0 : _a.trim().substring(0, 100)) || el.getAttribute('aria-label') || '';
        const href = el.href || '';
        this.track('click', {
            text,
            href,
            tag: el.tagName.toLowerCase(),
            has_data_cta: el.hasAttribute('data-cta'),
        });
    }
    onVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            if (this.isVisible) {
                this.totalEngagedMs += Date.now() - this.pageEnteredAt;
                this.isVisible = false;
            }
        }
        else {
            this.pageEnteredAt = Date.now();
            this.isVisible = true;
        }
    }
    onNavigation() {
        // Close out previous page
        this.closeCurrentPage();
        // Record new page (raw URL — AI will interpret)
        this.recordPageView(location.href);
        // Reset scroll depth for new page
        this.maxScrollDepth = 0;
    }
    onHashChange() {
        // Record hash navigation as raw data
        const hash = location.hash;
        if (!hash)
            return;
        this.track('hash_change', { url: location.pathname + hash });
    }
    onBeforeUnload() {
        // Close out current page
        this.closeCurrentPage();
        // Record scroll depth for current page
        if (this.maxScrollDepth > 0) {
            this.track('scroll_depth', { depth: this.maxScrollDepth });
        }
        // Record total engaged time
        this.track('session_time', {
            engaged_ms: this.totalEngagedMs + (this.isVisible ? Date.now() - this.pageEnteredAt : 0),
        });
        // Flush everything immediately
        this.flush();
    }
    // -------------------------------------------------------------------------
    // Listener Management
    // -------------------------------------------------------------------------
    addListeners() {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        document.addEventListener('click', this.handleClick, { passive: true, capture: true });
        document.addEventListener('visibilitychange', this.handleVisibility);
        window.addEventListener('popstate', this.handlePopState);
        window.addEventListener('hashchange', this.handleHashChange);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        // Intercept pushState / replaceState for SPA navigation
        history.pushState = (...args) => {
            this.origPushState(...args);
            this.onNavigation();
        };
        history.replaceState = (...args) => {
            this.origReplaceState(...args);
            this.onNavigation();
        };
    }
    removeListeners() {
        window.removeEventListener('scroll', this.handleScroll);
        document.removeEventListener('click', this.handleClick, { capture: true });
        document.removeEventListener('visibilitychange', this.handleVisibility);
        window.removeEventListener('popstate', this.handlePopState);
        window.removeEventListener('hashchange', this.handleHashChange);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        // Restore original history methods
        history.pushState = this.origPushState;
        history.replaceState = this.origReplaceState;
    }
    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    flush() {
        if (this.queue.length === 0)
            return;
        const payload = JSON.stringify({
            widget_id: this.config.widgetId,
            visitor_id: this.config.visitorId,
            events: this.queue,
            url: location.href,
            timestamp: Date.now(),
        });
        this.queue = [];
        // sendBeacon is fire-and-forget — survives page unload
        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(this.config.endpoint, blob);
        }
        else {
            fetch(this.config.endpoint, {
                method: 'POST',
                body: payload,
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
            }).catch(() => {
                // Silently fail — never disrupt host site
            });
        }
    }
    recordPageView(url) {
        this.pagesViewed.push({ url, enteredAt: Date.now() });
        this.pageEnteredAt = Date.now();
        this.track('page_view', { url: new URL(url).pathname });
    }
    closeCurrentPage() {
        const current = this.pagesViewed[this.pagesViewed.length - 1];
        if (current && !current.duration_ms) {
            current.duration_ms = Date.now() - current.enteredAt;
        }
        if (this.isVisible) {
            this.totalEngagedMs += Date.now() - this.pageEnteredAt;
            this.pageEnteredAt = Date.now();
        }
        if (this.maxScrollDepth > 0) {
            this.track('scroll_depth', { depth: this.maxScrollDepth, url: location.pathname });
        }
    }
    detectReturnVisit() {
        try {
            return localStorage.getItem('zalena_tracker_seen') !== null;
        }
        catch (_a) {
            return false;
        }
    }
    markVisitorSeen() {
        try {
            localStorage.setItem('zalena_tracker_seen', Date.now().toString());
        }
        catch (_a) {
            // localStorage unavailable
        }
    }
    extractUtmParams() {
        const params = {};
        try {
            const search = new URLSearchParams(location.search);
            for (const key of [
                'utm_source',
                'utm_medium',
                'utm_campaign',
                'utm_term',
                'utm_content',
                'gclid',
                'fbclid',
            ]) {
                const val = search.get(key);
                if (val)
                    params[key] = val;
            }
        }
        catch (_a) {
            // ignore
        }
        return params;
    }
    throttle(fn, ms) {
        let last = 0;
        return () => {
            const now = Date.now();
            if (now - last >= ms) {
                last = now;
                fn();
            }
        };
    }
}
// ---------------------------------------------------------------------------
// Singleton — used by the widget
// ---------------------------------------------------------------------------
let trackerInstance = null;
/** Initialize the global tracker. Call once from standalone-bundle-root. */
export function initTracker(config) {
    if (trackerInstance)
        return trackerInstance;
    trackerInstance = new VisitorTracker(config);
    trackerInstance.start();
    return trackerInstance;
}
/** Get the running tracker instance (for tracking chat events from components). */
export function getTracker() {
    return trackerInstance;
}
